import Blog from "./blog";
import { useEffect, useState } from "react";
import CustomButton from "../../common/button";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import type { FilterType } from "./blogArticleSection";
import type { JsonApiResource } from "next-drupal";
import { type FilterTagWithType } from "./tagFilter";

export type ArticleGridProps = {
  title?: string | JSX.Element;
  subtitle?: string;
  body?: string | string[] | JSX.Element;
  filterTags?: FilterTagWithType[];
  articles?: JsonApiResource[] | null; // all articles
  currentArticleId?: string | number;
  minShow?: number; // minimum number of articles to show this section.  defaults to 2 -- i.e. the entire section is hidden when there are less than 2 articles
};

const DEFAULT_MIN_SHOW = 2;

export default function ArticleGridSection({
  title,
  subtitle,
  filterTags,
  articles,
  currentArticleId,
  ...props
}: ArticleGridProps) {
  const body = Array.isArray(props.body)
    ? props.body.map((line: string, i: number) => (
        <div key={i} className="mt-2">
          {line}
        </div>
      ))
    : props.body;

  const minShow = props.minShow ?? DEFAULT_MIN_SHOW;

  const [filterArticles, setFilterArticles] = useState<JsonApiResource[]>();
  const [tagFilter, setTagFilter] = useState<FilterTagWithType[] | undefined>(
    filterTags,
  );

  // filtered related artiles on tags change
  const filterRelatedArticles = articles?.filter((article) => {
    if (tagFilter?.length === 0) return true; // show all articles when no filters applied

    const currentFilterTags = tagFilter?.map((tag: { tag: string }) => tag.tag);

    // check if article matches any active filter
    const tagMatches = article.field_tags.some((tag: { name: string }) =>
      currentFilterTags?.includes(tag.name),
    );
    return article.id !== currentArticleId && tagMatches;
  });

  const handleTagClick = (tag: FilterTagWithType) => {
    setTagFilter((prev): FilterTagWithType[] => {
      if (prev?.length === 1 && prev[0] === tag && filterTags) {
        return filterTags;
      }
      return [tag];
    });
  };

  // update blogs when filter changed
  useEffect(() => {
    setFilterArticles(filterRelatedArticles);
  }, [tagFilter]);

  // update button dynamically
  const singleTag = tagFilter?.length === 1 ? tagFilter[0] : "";
  const seeMoreText = `See more ${singleTag ? `${singleTag.tag}` : ""} articles`;

  const href =
    // no filters: dynamic filtering off, just the generic articles page
    !filterTags?.length
      ? "/insights"
      : // only one filter: dynamic filtering off, has to be this one
        filterTags?.length === 1
        ? `/insights?${filterTags[0]?.type}=${filterTags[0]?.tag}`
        : // multiple filters: dynamic filtering on
          // filter selected: that filter
          singleTag
          ? `/insights?${singleTag.type}=${singleTag.tag}`
          : "/insights";

  return (filterArticles?.length ?? 0) < minShow || filterTags?.length === 0 ? (
    <>
      <CustomButton className="mx-auto mb-24 w-fit" href={href}>
        <span>{seeMoreText}</span>
        <ArrowRightIcon className="ml-2 h-5 w-5" />
      </CustomButton>
    </>
  ) : (
    <div className="mx-auto w-fit max-w-7xl px-6 pt-4 lg:px-8 2xl:px-0">
      {(title || subtitle) && (
        <div className="mx-auto max-w-2xl pb-8 text-center text-green-light">
          {title && (
            <h2 className="text-resp-4xl font-bold tracking-tight">{title}</h2>
          )}
          {subtitle && (
            <p className="text-resp-base mt-2 leading-relaxed text-green-light">
              {subtitle}
            </p>
          )}
          {props.body && <div className={`mt-4`}>{body}</div>}
        </div>
      )}

      {filterTags && filterTags.length > 0 && (
        <div className="mb-6 text-center">
          {filterTags?.map((tag, i: number) => (
            <button
              key={i}
              onClick={() => handleTagClick(tag)}
              className={`relative z-10 ml-2 rounded-full px-3 py-1.5 font-semibold hover:bg-white/20 ${tagFilter?.length === 1 && tagFilter?.includes(tag) ? "bg-green/20 text-green" : "bg-white/10 text-white"}`}
            >
              {tag.tag}
            </button>
          ))}
        </div>
      )}

      {filterArticles && filterArticles.length > 0 && (
        <div className="mx-auto mb-6 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-20 pt-8 lg:mx-0 lg:max-w-none lg:grid-cols-3">
          {filterArticles?.map((blog, i) => (
            <Blog
              key={i}
              blog={blog}
              tagColor="light"
              resetFilters={function (
                type: keyof FilterType,
                value: string,
                // eslint-disable-next-line @typescript-eslint/no-empty-function
              ): void {}}
            />
          ))}
        </div>
      )}

      <CustomButton className="mx-auto mb-24 w-fit" href={href}>
        <span>{seeMoreText}</span>
        <ArrowRightIcon className="ml-2 h-5 w-5" />
      </CustomButton>
    </div>
  );
}
